import { useEffect, useState } from 'react'
import { API_BASE } from '../lib/api'
import BlogsPanel from './blogs'
import AdminRestaurantPanel from './adminRestaurant'
import AdminReferralPanel from './adminReferral'
import AdminCampaignPanel from './admincampaign'
import AdminQrScansPanel from './adminQrScans'

const ADMIN_TOKEN_KEY = 'coeater_admin_token'

export default function AdminPage() {
	const [adminToken, setAdminToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '')
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [adminInfo, setAdminInfo] = useState(null)
	const [activePanel, setActivePanel] = useState(null)

	useEffect(() => {
		let mounted = true
		if (!adminToken) {
			setLoading(false)
			return undefined
		}

		async function verifyAdmin() {
			try {
				const res = await fetch(`${API_BASE}/admin/me`, {
					headers: { Authorization: `Bearer ${adminToken}` },
				})
				if (!res.ok) throw new Error('Session expired. Please login again.')
				const data = await res.json()
				if (mounted) {
					setAdminInfo(data.admin)
					setError('')
				}
			} catch (err) {
				if (mounted) {
					setAdminToken('')
					localStorage.removeItem(ADMIN_TOKEN_KEY)
					setError(err.message)
				}
			} finally {
				if (mounted) setLoading(false)
			}
		}

		verifyAdmin()
		return () => { mounted = false }
	}, [adminToken])

	async function handleLogin(e) {
		e.preventDefault()
		setSubmitting(true)
		setError('')

		try {
			const res = await fetch(`${API_BASE}/admin/login`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, password }),
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || 'Admin login failed.')

			localStorage.setItem(ADMIN_TOKEN_KEY, data.token)
			setAdminToken(data.token)
			setAdminInfo(data.admin)
			setPassword('')
			setActivePanel(null)
		} catch (err) {
			setError(err.message)
		} finally {
			setSubmitting(false)
		}
	}

	function logout() {
		localStorage.removeItem(ADMIN_TOKEN_KEY)
		setAdminToken('')
		setAdminInfo(null)
		setEmail('')
		setPassword('')
		setActivePanel(null)
	}

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4 py-8 sm:px-8">
				<div className="mx-auto max-w-2xl rounded-3xl border border-orange-100 bg-white/80 p-8 text-center shadow-sm">
					<p className="text-sm font-semibold text-gray-600">Checking admin session...</p>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4 py-8 sm:px-8">
			<div className="mx-auto max-w-6xl space-y-6">
				<header className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
					<p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Admin Panel</p>
					<h1 className="mt-2 title-font text-3xl font-extrabold text-gray-900">Co-Eater Content Console</h1>
					<p className="mt-2 text-sm text-gray-600">Login as admin to publish blogs and add restaurants with Azure Blob image URLs.</p>
				</header>

				{!adminToken ? (
					<form onSubmit={handleLogin} className="mx-auto max-w-xl rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
						<h2 className="title-font text-xl font-bold text-gray-900">Admin Login</h2>
						<div className="mt-4 space-y-3">
							<input
								type="email"
								value={email}
								onChange={e => setEmail(e.target.value)}
								placeholder="Admin email"
								className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm"
								required
							/>
							<input
								type="password"
								value={password}
								onChange={e => setPassword(e.target.value)}
								placeholder="Admin password"
								className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm"
								required
							/>
							<button
								type="submit"
								disabled={submitting}
								className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-60"
							>
								{submitting ? 'Logging in...' : 'Login'}
							</button>
							{error && <p className="text-sm font-medium text-red-600">{error}</p>}
						</div>
					</form>
				) : (
					<>
						<div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-orange-100 bg-white/80 p-4 shadow-sm">
							<p className="text-sm font-semibold text-gray-700">Signed in as {adminInfo?.email || 'admin'}</p>
							<button onClick={logout} className="rounded-full border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
								Logout
							</button>
						</div>

						<div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
							<h2 className="title-font text-xl font-bold text-gray-900">Choose Action</h2>
							<p className="mt-1 text-sm text-gray-600">Select what you want to create.</p>
							<div className="mt-4 flex flex-wrap gap-3">
								<button
									type="button"
									onClick={() => setActivePanel('blog')}
									className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
										activePanel === 'blog'
											? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
											: 'border border-orange-300 text-orange-700 hover:bg-orange-50'
									}`}
								>
									Add Blog
								</button>
								<button
									type="button"
									onClick={() => setActivePanel('restaurant')}
									className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
										activePanel === 'restaurant'
											? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
											: 'border border-orange-300 text-orange-700 hover:bg-orange-50'
									}`}
								>
									Add Restaurant
								</button>
								<button
									type="button"
									onClick={() => setActivePanel('referral')}
									className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
										activePanel === 'referral'
											? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
											: 'border border-orange-300 text-orange-700 hover:bg-orange-50'
									}`}
								>
									Referral Payouts
								</button>
							<button
								type="button"
								onClick={() => setActivePanel('campaigns')}
								className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
									activePanel === 'campaigns'
										? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
										: 'border border-orange-300 text-orange-700 hover:bg-orange-50'
								}`}
							>
								🚀 Campaigns
							</button>
							<button
								type="button"
								onClick={() => setActivePanel('qr-scans')}
								className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
									activePanel === 'qr-scans'
										? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
										: 'border border-orange-300 text-orange-700 hover:bg-orange-50'
								}`}
							>
								📷 QR Scans
							</button>
						</div>
					</div>

					{activePanel === 'blog' && <BlogsPanel adminToken={adminToken} />}
					{activePanel === 'restaurant' && <AdminRestaurantPanel adminToken={adminToken} />}
					{activePanel === 'referral' && <AdminReferralPanel adminToken={adminToken} />}
					{activePanel === 'campaigns' && <AdminCampaignPanel adminToken={adminToken} />}
					{activePanel === 'qr-scans' && <AdminQrScansPanel adminToken={adminToken} />}
					</>
				)}
			</div>
		</div>
	)
}
